import { useState } from "react";
import { formatRevenue } from "../../../utils/revenueFormat";
import "../../../css/AdCategoryTable.css";

// 환불액 컬럼 헤더 ? 툴팁 문구 (모바일에서는 hover가 없으므로 표 아래 각주로 함께 노출)
const REFUND_HINT = "해당 월 동안 환불 처리 된 거래의 환불 금액 합계, 매출액과 별도 참고 지표.";

const SORT_ORDER = [
  "플레이스 리워드",
  "블로그 상품",
  "언론 기사 배포",
  "악성리뷰 관리",
  "맘카페·당근",
  "체험단",
  "촬영패키지",
  "로고 디자인",
  "카카오 친구 추가",
  "당근 단골 맺기",
  "카카오맵 리뷰 홍보",
  "쇼핑 리워드",
  "카카오맵 리워드",
  "지역 TV 광고",
  "임차인 중개보수",
];

function sortByOrder(categories) {
  return [...categories].sort((a, b) => {
    const ai = SORT_ORDER.indexOf(a.metaName);
    const bi = SORT_ORDER.indexOf(b.metaName);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

function renderMomBadge(mom, { emptyOnNull = false } = {}) {
  if (mom == null)
    return emptyOnNull ? null : (
      <span className="cat-mom cat-mom-neutral">기준월</span>
    );
  if (Math.abs(mom) < 0.0005)
    return <span className="cat-mom cat-mom-neutral">— 0.0%</span>;
  const pct = Math.abs(mom * 100).toFixed(1);
  if (mom > 0) return <span className="cat-mom cat-mom-up">▲ {pct}%</span>;
  if (mom < 0) return <span className="cat-mom cat-mom-down">▼ {pct}%</span>;
  return <span className="cat-mom cat-mom-neutral">— 0.0%</span>;
}

// 최상위로 오는 항목을 부모 카테고리의 subTypes로 묶는 전처리
const VIRTUAL_CHILDREN = {
  "블로그 상품": new Set([
    "최적화 블로그 배포",
    "최적화 블로그 원고",
    "블로그 기자단 배포",
    "실명 인증 기자단",
    "사진필요없는 블로그배포",
  ]),
  "맘카페·당근": new Set(["맘 카페 게시글 배포", "당근 우리동네 게시글 배포"]),
  체험단: new Set(["일본인 체험단", "샤오홍슈 체험단"]),
  촬영패키지: new Set([
    "브랜딩 촬영 패키지",
    "촬영상품 골라담기",
    "블로그 배포용 대량 촬영",
  ]),
};

const ALL_CHILD_NAMES = new Set(
  Object.values(VIRTUAL_CHILDREN).flatMap((s) => [...s]),
);

function groupVirtualSubtypes(categories) {
  const collected = {};
  const main = [];

  for (const cat of categories) {
    if (ALL_CHILD_NAMES.has(cat.metaName)) {
      for (const [parent, children] of Object.entries(VIRTUAL_CHILDREN)) {
        if (children.has(cat.metaName)) {
          if (!collected[parent]) collected[parent] = [];
          collected[parent].push({
            name: cat.metaName,
            count: cat.count,
            revenue: cat.revenue,
            refund: cat.refund,
            ratio: cat.ratio,
            mom: cat.mom,
          });
        }
      }
    } else {
      main.push(cat);
    }
  }

  const result = main.map((cat) => {
    const extra = collected[cat.metaName];
    if (extra?.length > 0) {
      return { ...cat, subTypes: [...(cat.subTypes || []), ...extra] };
    }
    return cat;
  });

  // 가상 부모가 API에 없으면 직접 생성
  const existingNames = new Set(result.map((c) => c.metaName));
  for (const [parentName] of Object.entries(VIRTUAL_CHILDREN)) {
    if (!existingNames.has(parentName) && collected[parentName]?.length > 0) {
      const subTypes = collected[parentName];
      result.push({
        metaId: `virtual-${parentName}`,
        metaName: parentName,
        count: subTypes.reduce((s, c) => s + (c.count ?? 0), 0),
        revenue: subTypes.reduce((s, c) => s + (c.revenue ?? 0), 0),
        refund: subTypes.reduce((s, c) => s + (c.refund ?? 0), 0),
        ratio: null,
        mom: null,
        subTypes,
        subGroups: [],
      });
    }
  }
  return result;
}

function AdCategoryTable({ categories }) {
  const grouped = groupVirtualSubtypes(categories);
  const zeroRevenueCategories = grouped.filter(
    (cat) => (cat.revenue ?? 0) === 0 && (cat.refund ?? 0) === 0,
  );
  const visibleCategories = grouped.filter(
    (cat) => (cat.revenue ?? 0) !== 0 || (cat.refund ?? 0) !== 0,
  );
  const processed = sortByOrder(visibleCategories);
  const [expandedIds, setExpandedIds] = useState(new Set());

  if (processed.length === 0) {
    return <p className="revenue-empty-inline">표시할 데이터가 없습니다.</p>;
  }

  const toggleExpand = (metaId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(metaId)) {
        next.delete(metaId);
      } else {
        next.add(metaId);
      }
      return next;
    });
  };

  const VIRTUAL_PARENT_NAMES = new Set(Object.keys(VIRTUAL_CHILDREN));

  const rows = processed.flatMap((cat) => {
    const isVirtualParent =
      VIRTUAL_PARENT_NAMES.has(cat.metaName) && cat.subTypes?.length > 0;
    const hasChildren = isVirtualParent || cat.subGroups?.length > 0;
    const isExpanded = expandedIds.has(cat.metaId);

    const result = [
      <tr
        key={`cat-${cat.metaId}`}
        className={`${hasChildren ? "cat-row-expandable" : ""} ${isExpanded ? "cat-row-expanded" : ""}`}
        onClick={hasChildren ? () => toggleExpand(cat.metaId) : undefined}
      >
        <td>
          {hasChildren && (
            <button className={`cat-expand-btn${isExpanded ? " open" : ""}`}>
              ▶
            </button>
          )}
          {hasChildren && " "}
          {cat.metaName}
        </td>
        <td>{cat.count}건</td>
        <td>{formatRevenue(cat.revenue)}</td>
        <td style={{ color: (cat.refund ?? 0) > 0 ? "#D85A30" : "#aaa" }}>
          {(cat.refund ?? 0) > 0
            ? `-${formatRevenue(cat.refund)}`
            : formatRevenue(cat.refund)}
        </td>
        <td>{cat.ratio != null ? (cat.ratio * 100).toFixed(1) + "%" : "-"}</td>
        <td>{renderMomBadge(cat.mom)}</td>
      </tr>,
    ];

    // subGroups 있는 카테고리: 그룹 헤더 → 해당 그룹 subTypes 인터리빙
    // group: null인 subType은 유입/저장 구분 없는 단순 항목이므로 제외
    if (isExpanded && cat.subGroups?.length > 0) {
      cat.subGroups.forEach((group) => {
        result.push(
          <tr
            key={`group-${cat.metaId}-${group.group}`}
            className="cat-group-header"
          >
            <td>📁 {group.group} ({group.count}개)</td>
            <td>{group.count}건</td>
            <td>{formatRevenue(group.revenue)}</td>
            <td style={{ color: (group.refund ?? 0) > 0 ? "#D85A30" : "#aaa" }}>
              {(group.refund ?? 0) > 0
                ? `-${formatRevenue(group.refund)}`
                : formatRevenue(group.refund)}
            </td>
            <td>
              {cat.revenue > 0
                ? ((group.revenue / cat.revenue) * 100).toFixed(1) + "%"
                : "-"}
            </td>
            <td></td>
          </tr>,
        );
        (cat.subTypes || [])
          .filter((st) => st.group === group.group)
          .forEach((subType, i) => {
            result.push(
              <tr
                key={`subtype-${cat.metaId}-${group.group}-${i}`}
                className="cat-subrow"
              >
                <td>{subType.name}</td>
                <td>{subType.count}건</td>
                <td>{formatRevenue(subType.revenue)}</td>
                <td style={{ color: (subType.refund ?? 0) > 0 ? "#D85A30" : "#aaa" }}>
                  {(subType.refund ?? 0) > 0
                    ? `-${formatRevenue(subType.refund)}`
                    : formatRevenue(subType.refund)}
                </td>
                <td>
                  {cat.revenue > 0
                    ? ((subType.revenue / cat.revenue) * 100).toFixed(1) + "%"
                    : "-"}
                </td>
                <td></td>
              </tr>,
            );
          });
      });
    }

    // VIRTUAL_CHILDREN 부모 (subGroups 없음): subTypes 직접 나열
    if (isExpanded && isVirtualParent && cat.subTypes?.length > 0) {
      cat.subTypes.forEach((subType, i) => {
        result.push(
          <tr
            key={`subtype-${cat.metaId}-${i}`}
            className="cat-subrow"
          >
            <td>{subType.name}</td>
            <td>{subType.count}건</td>
            <td>{formatRevenue(subType.revenue)}</td>
            <td style={{ color: (subType.refund ?? 0) > 0 ? "#D85A30" : "#aaa" }}>
              {(subType.refund ?? 0) > 0
                ? `-${formatRevenue(subType.refund)}`
                : formatRevenue(subType.refund)}
            </td>
            <td>
              {subType.ratio != null
                ? (subType.ratio * 100).toFixed(1) + "%"
                : "-"}
            </td>
            <td>{renderMomBadge(subType.mom, { emptyOnNull: true })}</td>
          </tr>,
        );
      });
    }

    return result;
  });

  return (
    <>
      <p className="cat-guide">
        💡 카테고리명에 ▶ 표시가 있는 항목은 클릭하면 하위 유형별로 펼쳐집니다
      </p>
      <div className="ad-category-table-scroll">
        <table className="ad-category-table">
          <colgroup>
            <col style={{ width: "28%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "17%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "18%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>카테고리</th>
              <th>건수</th>
              <th>매출액</th>
              <th>
                <span className="th-hint-wrap">
                  환불액
                  <span className="th-hint-icon">?</span>
                  <span className="th-hint-popup">{REFUND_HINT}</span>
                </span>
              </th>
              <th>비중</th>
              <th>MoM</th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </div>
      <ul className="th-hint-notes">
        <li><strong>※ 환불액</strong> {REFUND_HINT}</li>
      </ul>
      {zeroRevenueCategories.length >= 5 && (
        <p className="cat-zero-notice">
          이번 달 매출이 없는 카테고리 {zeroRevenueCategories.length}개
        </p>
      )}
    </>
  );
}

export default AdCategoryTable;
